import { useEffect, useRef, useState } from "react";
import {
    Bell, Bookmark, Check, ChevronDown, ChevronRight, CircleDollarSign, Crosshair, Globe2, Heart, HelpCircle,
    History, Languages, LogOut, MapPin, Menu, Navigation, Package, Search, ShoppingBag, ShoppingCart, Store,
    User, UserRound, UtensilsCrossed, WalletCards, X, Zap,
} from "lucide-react";
import { useNavigate, } from "react-router-dom";
import "./HomeNavbar.css";
import { useCart } from "../../../context/CartContext";
import { useWishlist } from "../../../context/WishlistContext";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { useAuth } from "../../../context/AuthContext";
import { logout } from "../../../Redux/custmoer_auth/slice";
import { setDeliveryLocation } from "../../../Redux/delivery-location/slice";
import {
    getAllAddresses,
    setDefaultAddress,
} from "../../../Redux/customer-address/slice";
import {
    requestCurrentPosition,
    reverseGeocodeCoordinates,
    searchLocations,
} from "../../../utility/location";
import {
    getSuggestions,
    clearSuggestions,
} from "../../../Redux/Search_apis/slice";
import { SuggestionResponseDto } from "../../../Redux/Search_apis/types";
import {
    recordActivity,
    getRecentSearches,
} from "../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../Redux/ActivityApi/types";
import { navbarSearchCategories } from "./HomeNavbarSearchData";
import { ADDRESS_SETUP_EVENT } from "../LocationSetupModal/LocationSetupData";
type Language = {
    name: string;
    shortName: string;
    flag: string;
};
type Country = {
    name: string;
    code: string;
    flag: string;
};
type Currency = {
    code: string;
    symbol: string;
    name: string;
};
type Address = {
    id: string | number;
    label: string;
    address: string;
    type: "Home" | "Work" | "Other";
    _raw?: any;
};
type ShoppingMode = "shopping" | "quick" | "food" | "stores";
type HomeNavbarProps = {
    variant?: "home" | "detail";
};
const languages: Language[] = [
    {
        name: "English",
        shortName: "EN",
        flag: "🇬🇧",
    },
    {
        name: "Hindi",
        shortName: "HI",
        flag: "🇮🇳",
    },
    {
        name: "Telugu",
        shortName: "TE",
        flag: "🇮🇳",
    },
    {
        name: "Tamil",
        shortName: "TA",
        flag: "🇮🇳",
    },
    {
        name: "Kannada",
        shortName: "KN",
        flag: "🇮🇳",
    },
    {
        name: "Malayalam",
        shortName: "ML",
        flag: "🇮🇳",
    },
];
const countries: Country[] = [
    { name: "India", code: "IN", flag: "🇮🇳" },
    { name: "United States", code: "US", flag: "🇺🇸" },
    { name: "United Kingdom", code: "GB", flag: "🇬🇧" },
    { name: "United Arab Emirates", code: "AE", flag: "🇦🇪" },
    { name: "Canada", code: "CA", flag: "🇨🇦" },
    { name: "Australia", code: "AU", flag: "🇦🇺" },
    { name: "Singapore", code: "SG", flag: "🇸🇬" },
];
const currencies: Currency[] = [
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "GBP", symbol: "£", name: "British Pound" },
    { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
];
const shoppingModes: {
    id: ShoppingMode;
    label: string;
    subtitle: string;
    icon: React.ReactNode;
}[] = [
        {
            id: "shopping",
            label: "Salar Shopping",
            subtitle: "Marketplace",
            icon: (<ShoppingBag size={16} strokeWidth={1.9} />),
        },
        {
            id: "quick",
            label: "Quick Minutes",
            subtitle: "Fast Delivery",
            icon: (<Zap size={16} strokeWidth={2} />),
        },
        {
            id: "food",
            label: "Food",
            subtitle: "Salar Eats",
            icon: (<UtensilsCrossed size={16} strokeWidth={1.9} />),
        },
        {
            id: "stores",
            label: "Stores",
            subtitle: "Nearby",
            icon: (<Store size={16} strokeWidth={1.9} />),
        },
    ];

const formatCartPrice = (value: number) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2, }).format(value);
const HomeNavbar = ({ variant = "home", }: HomeNavbarProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isAuthenticated, user } = useAuth();
    const { cartCount, cartTotal, setCartOpen, } = useCart();
    const { wishlistCount } = useWishlist();
    const { location } = useAppSelector((state) => state.deliveryLocation);
    const { addresses } = useAppSelector((state) => state.customerAddress);
    const suggestions = useAppSelector(
        (state) => state.search.suggestions.data
    );
    const recentSearches = useAppSelector(
        (state) => state.activity.recentSearches
    );

    const [searchTerm, setSearchTerm,] = useState("");
    const [showSuggestions, setShowSuggestions,] = useState(false);
    const searchRef = useRef<HTMLFormElement>(null);

    const displayAddresses: Address[] = (addresses || []).map((addr) => {
        const full = [
            addr.addressLine1,
            addr.addressLine2,
            addr.city,
            addr.state,
            addr.pincode,
        ]
            .filter(Boolean)
            .join(", ");
        const addrType =
            addr.addressType === "Office" || addr.addressType === "Work"
                ? "Work"
                : addr.addressType === "Home"
                ? "Home"
                : "Other";
        return {
            id: addr._id || String(Math.random()),
            label: addr.fullName || (addr.addressType ? `${addr.addressType} Address` : "Address"),
            address: full,
            type: addrType,
            _raw: addr,
        };
    });

    const [shoppingMode, setShoppingMode,] = useState<ShoppingMode>("shopping");
    const [globalShopping, setGlobalShopping,] = useState(false);
    const [locationOpen, setLocationOpen,] = useState(false);
    const [localeOpen, setLocaleOpen,] = useState(false);
    const [profileOpen, setProfileOpen,] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen,] = useState(false);
    const [countryOpen, setCountryOpen,] = useState(false);
    const [languageOpen, setLanguageOpen,] = useState(false);
    const [currencyOpen, setCurrencyOpen,] = useState(false);
    const [manualLocationOpen, setManualLocationOpen,] = useState(false);
    const [detectingLocation, setDetectingLocation,] = useState(false);
    const [country, setCountry,] = useState("IN");
    const [language, setLanguage,] = useState("English");
    const [currency, setCurrency,] = useState("INR");
    const [manualLocation, setManualLocation,] = useState("");
    const [searchCategoryOpen, setSearchCategoryOpen,] = useState(false);
    const [categorySearch, setCategorySearch,] = useState("");
    const [selectedSearchCategoryId, setSelectedSearchCategoryId,] = useState("all");
    const [hiddenSearches, setHiddenSearches,] = useState<string[]>([]);
    const [selectedAddress, setSelectedAddress,] = useState<Address>({
        id: "unset",
        label: "Delivery Location",
        address: "Select delivery location",
        type: "Other",
    });

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm.trim().length > 1) {
                dispatch(getSuggestions(searchTerm));
                setShowSuggestions(true);
            } else {
                dispatch(clearSuggestions());
            }
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(getAllAddresses());
        }
    }, [dispatch, isAuthenticated]);

    useEffect(() => {
        if (addresses && addresses.length > 0) {
            const defaultAddr = addresses.find((a) => a.defaultAddress) || addresses[0];
            const fullAddress = [
                defaultAddr.addressLine1,
                defaultAddr.addressLine2,
                defaultAddr.city,
                defaultAddr.state,
                defaultAddr.pincode,
            ]
                .filter(Boolean)
                .join(", ");
            const addrType =
                defaultAddr.addressType === "Office" || defaultAddr.addressType === "Work"
                    ? "Work"
                    : defaultAddr.addressType === "Home"
                    ? "Home"
                    : "Other";
            setSelectedAddress({
                id: defaultAddr._id || "default",
                label: defaultAddr.fullName || defaultAddr.addressType || "Home",
                address: fullAddress,
                type: addrType,
                _raw: defaultAddr,
            });
        } else if (location && location.displayName) {
            setSelectedAddress({
                id: "persisted-loc",
                label: location.label || "Delivery Location",
                address: location.displayName,
                type: "Other",
            });
        }
    }, [addresses, location]);
    const locationRef = useRef<HTMLDivElement>(null);
    const localeRef = useRef<HTMLDivElement>(null);
    const profileRef = useRef<HTMLDivElement>(null);
    const searchCategoryRef = useRef<HTMLDivElement>(null);
    const selectedCountry = countries.find((item) => item.code === country) || countries[0];
    const selectedLanguage = languages.find((item) => item.name === language) || languages[0];
    const selectedCurrency = currencies.find((item) => item.code === currency) || currencies[0];
    const selectedSearchCategory = navbarSearchCategories.find((item) => item.id === selectedSearchCategoryId) || navbarSearchCategories[0];
    const normalizedCategorySearch = categorySearch.trim().toLowerCase();
    const filteredSearchCategories = navbarSearchCategories.filter((item) => {
        if (!normalizedCategorySearch) {
            return true;
        }
        return (
            item.label.toLowerCase().includes(normalizedCategorySearch) ||
            item.keywords.some((keyword) => keyword.toLowerCase().includes(normalizedCategorySearch))
        );
    });
    const visibleSearchSuggestions = (searchTerm.trim()
        ? suggestions.map((suggestion: SuggestionResponseDto) => suggestion.text)
        : (recentSearches || [])
    ).filter((term: string) => !hiddenSearches.includes(term));
    const searchSuggestionsTitle = searchTerm.trim() ? "Related searches" : "Recent searches";
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (locationRef.current && !locationRef.current.contains(target)) {
                setLocationOpen(false);
                setManualLocationOpen(false);
            }
            if (localeRef.current && !localeRef.current.contains(target)) {
                setLocaleOpen(false);
                setCountryOpen(false);
                setLanguageOpen(false);
                setCurrencyOpen(false);
            }
            if (profileRef.current && !profileRef.current.contains(target)) {
                setProfileOpen(false);
            }
            if (searchCategoryRef.current && !searchCategoryRef.current.contains(target)) {
                setSearchCategoryOpen(false);
                setCategorySearch("");
            }
            if (
                searchRef.current &&
                !searchRef.current.contains(target)
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);
    const closeDropdowns = () => {
        setLocationOpen(false);
        setLocaleOpen(false);
        setProfileOpen(false);
        setManualLocationOpen(false);
        setCountryOpen(false);
        setLanguageOpen(false);
        setCurrencyOpen(false);
        setSearchCategoryOpen(false);
        setShowSuggestions(false);
        setCategorySearch("");
    };

    const handleLocationOpen = () => {
        if (displayAddresses.length === 0) {
            closeDropdowns();
            setMobileMenuOpen(false);
            window.dispatchEvent(new CustomEvent(ADDRESS_SETUP_EVENT));
            return;
        }
        const next = !locationOpen;
        closeDropdowns();
        setLocationOpen(next);
    };
    const handleLocaleOpen = () => {
        const next = !localeOpen;
        closeDropdowns();
        setLocaleOpen(next);
    };

    const handleProfileOpen = () => {
        const next = !profileOpen;
        closeDropdowns();
        setProfileOpen(next);
    };

    const handleSearchCategoryOpen = () => {
        const next = !searchCategoryOpen;
        closeDropdowns();
        setSearchCategoryOpen(next);
    };

    const handleSearchCategorySelect = (categoryId: string) => {
        setSelectedSearchCategoryId(categoryId);
        setSearchCategoryOpen(false);
        setCategorySearch("");
    };

    const handleSearchSuggestionSelect = (term: string) => {
        setSearchTerm(term);
        handleSearch(term);
    };

    const handleSearchSuggestionDelete = (term: string) => {
        setHiddenSearches((prev) =>
            prev.includes(term) ? prev : [...prev, term]
        );
        setShowSuggestions(true);
    };

    const handleLogout = () => {
        setProfileOpen(false);
        dispatch(logout());
    };

    const userInitial = (user?.fullName || user?.name || user?.email || "")?.trim()?.charAt(0)?.toUpperCase();
    const userName = user?.fullName || user?.name || (user?.email ? user.email.split("@")[0] : "My Account");
    const userSubtitle = user?.email || user?.phone || "Manage your account and shopping.";

    const handleCartClick = () => {
        closeDropdowns();
        setMobileMenuOpen(false);
        setCartOpen(false);
        navigate("/Cart");
    };
    const handleLogoClick =
        () => {
            closeDropdowns();
            setMobileMenuOpen(false);
            navigate("/HomePage");
        };
    const handleSelectAddress = (address: Address) => {
        setSelectedAddress(address);
        setLocationOpen(false);

        if (address._raw && address._raw._id) {
            if (isAuthenticated) {
                dispatch(setDefaultAddress(address._raw._id)).then(() => {
                    dispatch(getAllAddresses());
                });
            }
            dispatch(
                setDeliveryLocation({
                    label: address._raw.addressType ?? address.label,
                    area: address._raw.addressLine1,
                    city: address._raw.city,
                    state: address._raw.state,
                    pincode: address._raw.pincode,
                    displayName: address.address,
                    latitude: address._raw.latitude,
                    longitude: address._raw.longitude,
                })
            );
        } else {
            dispatch(
                setDeliveryLocation({
                    label: address.label,
                    displayName: address.address,
                })
            );
        }
    };

    const detectCurrentLocation = async () => {
        if (!navigator.geolocation) {
            setManualLocationOpen(true);
            return;
        }
        setDetectingLocation(true);
        try {
            const coords = await requestCurrentPosition();
            const details = await reverseGeocodeCoordinates(coords.latitude, coords.longitude);
            const resolvedAddress =
                details.displayName ||
                [details.area, details.city, details.state, details.pincode]
                    .filter(Boolean)
                    .join(", ");

            dispatch(
                setDeliveryLocation({
                    label: "Current Location",
                    area: details.area,
                    city: details.city,
                    state: details.state,
                    pincode: details.pincode,
                    displayName: resolvedAddress,
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                })
            );
            setSelectedAddress({
                id: "current-location",
                label: "Current Location",
                address: resolvedAddress,
                type: "Other",
            });
            setDetectingLocation(false);
            setLocationOpen(false);
        } catch {
            setDetectingLocation(false);
            setManualLocationOpen(true);
        }
    };
    const saveManualLocation = async () => {
        const value = manualLocation.trim();
        if (!value) { return; }

        try {
            const results = await searchLocations(value);
            if (results && results.length > 0) {
                const first = results[0];
                const resolved = first.displayName || value;
                dispatch(
                    setDeliveryLocation({
                        label: first.city || "Selected Location",
                        area: first.area,
                        city: first.city,
                        state: first.state,
                        pincode: first.pincode,
                        displayName: resolved,
                        latitude: first.latitude,
                        longitude: first.longitude,
                    })
                );
                setSelectedAddress({
                    id: first.placeId || Date.now().toString(),
                    label: first.city || "Selected Location",
                    address: resolved,
                    type: "Other",
                });
            } else {
                dispatch(
                    setDeliveryLocation({
                        label: "Selected Location",
                        displayName: value,
                    })
                );
                setSelectedAddress({
                    id: Date.now().toString(),
                    label: "Selected Location",
                    address: value,
                    type: "Other",
                });
            }
        } catch {
            dispatch(
                setDeliveryLocation({
                    label: "Selected Location",
                    displayName: value,
                })
            );
            setSelectedAddress({
                id: Date.now().toString(),
                label: "Selected Location",
                address: value,
                type: "Other",
            });
        }
        setManualLocation("");
        setManualLocationOpen(false);
        setLocationOpen(false);
    };

    const getSearchPlaceholder =
        () => {
            if (shoppingMode === "quick") {
                return "Search groceries and daily essentials";
            }
            if (shoppingMode === "food") {
                return "Search restaurants, dishes and cuisines";
            }
            if (shoppingMode === "stores") {
                return "Search stores and nearby outlets";
            }
            return "Search products, brands and categories";
        };

    const handleSearch = async (q: string) => {
        const query = q.trim();
        if (query) {
            await dispatch(
                recordActivity({
                    activities: {
                        eventType: ActivityType.SEARCH,
                        searchQuery: query,
                    },
                })
            );
            const superCat = "All";
            const params = new URLSearchParams({ q: query });
            if (shoppingMode && shoppingMode !== "shopping") {
                params.set("mode", shoppingMode);
            }
            if (selectedSearchCategoryId && selectedSearchCategoryId !== "all") {
                params.set("category", selectedSearchCategoryId);
            }
            navigate(`/Categories/${encodeURIComponent(superCat)}?${params.toString()}`);
            setShowSuggestions(false);
        }
    };

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        handleSearch(searchTerm);
    };

    const handleSearchFocus = () => {
        dispatch(getRecentSearches(5));
        setShowSuggestions(true);
    };

    const renderSearchBar = (className = "") => (
        <form
            ref={searchRef}
            className={`home-nav-search ${className}`}
            onSubmit={handleSearchSubmit}
        >
            <div
                className="home-nav-search-category"
                ref={searchCategoryRef}
            >
                <button
                    type="button"
                    className={`home-nav-search-category-trigger ${searchCategoryOpen ? "active" : ""}`}
                    onClick={handleSearchCategoryOpen}
                    aria-haspopup="listbox"
                    aria-expanded={searchCategoryOpen}
                >
                    <span className="home-nav-search-category-icon">
                        {selectedSearchCategory.icon}
                    </span>
                    <span className="home-nav-search-category-label">
                        {selectedSearchCategory.label}
                    </span>
                    <ChevronDown
                        size={14}
                        strokeWidth={2}
                        className={searchCategoryOpen ? "home-nav-rotate" : ""}
                    />
                </button>

                {searchCategoryOpen && (
                    <div className="home-nav-search-category-dropdown">
                        <div className="home-nav-search-category-header">
                            <div>
                                <span>SEARCH SCOPE</span>
                                <strong>Choose a category</strong>
                            </div>
                            <small>
                                {navbarSearchCategories.length - 1} categories
                            </small>
                        </div>

                        <label className="home-nav-search-category-filter">
                            <Search size={15} strokeWidth={1.9} />
                            <input
                                type="text"
                                value={categorySearch}
                                onChange={(event) =>
                                    setCategorySearch(event.target.value)
                                }
                                placeholder="Type a category..."
                                autoFocus
                            />
                            {categorySearch && (
                                <button
                                    type="button"
                                    onClick={() => setCategorySearch("")}
                                    aria-label="Clear category search"
                                >
                                    <X size={14} strokeWidth={2} />
                                </button>
                            )}
                        </label>

                        <div
                            className="home-nav-search-category-list"
                            role="listbox"
                        >
                            {filteredSearchCategories.length > 0 ? (
                                filteredSearchCategories.map((category) => {
                                    const selected =
                                        category.id === selectedSearchCategoryId;

                                    return (
                                        <button
                                            type="button"
                                            key={category.id}
                                            className={selected ? "selected" : ""}
                                            onClick={() =>
                                                handleSearchCategorySelect(
                                                    category.id
                                                )
                                            }
                                            role="option"
                                            aria-selected={selected}
                                        >
                                            <span className="home-nav-search-category-item-icon">
                                                {category.icon}
                                            </span>
                                            <span>
                                                <strong>{category.label}</strong>
                                            </span>
                                            <span className="home-nav-search-category-check">
                                                {selected && (
                                                    <Check
                                                        size={13}
                                                        strokeWidth={2.5}
                                                    />
                                                )}
                                            </span>
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="home-nav-search-category-empty">
                                    <Search size={18} strokeWidth={1.8} />
                                    <strong>No categories found</strong>
                                    <span>Try another category name.</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <span className="home-nav-search-divider" />

            <input
                type="text"
                value={searchTerm}
                onFocus={handleSearchFocus}
                onClick={handleSearchFocus}
                onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setShowSuggestions(true);
                }}
                placeholder={getSearchPlaceholder()}
                aria-label="Search Salar"
                autoComplete="off"
            />

            <button
                type="submit"
                className="home-nav-search-submit"
                aria-label="Search"
            >
                <Search size={19} strokeWidth={2.1} />
            </button>

            {showSuggestions && !searchCategoryOpen && (
                <div className="home-nav-search-history-dropdown">
                    <div className="home-nav-search-history-header">
                        <div>
                            <span>SEARCH SALAR</span>
                            <strong>{searchSuggestionsTitle}</strong>
                        </div>

                        <small>
                            {visibleSearchSuggestions.length} suggestions
                        </small>
                    </div>

                    <div className="home-nav-search-history-list">
                        {visibleSearchSuggestions.length > 0 ? (
                            visibleSearchSuggestions.map((term) => (
                                <div
                                    className="home-nav-search-history-item"
                                    key={term}
                                >
                                    <button
                                        type="button"
                                        className="home-nav-search-history-select"
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={() =>
                                            handleSearchSuggestionSelect(term)
                                        }
                                    >
                                        <span className="home-nav-search-history-icon">
                                            <History size={16} strokeWidth={1.9} />
                                        </span>

                                        <span>{term}</span>
                                    </button>

                                    <button
                                        type="button"
                                        className="home-nav-search-history-remove"
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={() =>
                                            handleSearchSuggestionDelete(term)
                                        }
                                        aria-label={`Remove ${term} from recent searches`}
                                    >
                                        <X size={16} strokeWidth={2} />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="home-nav-search-history-empty">
                                <Search size={19} strokeWidth={1.8} />

                                <strong>No matching searches</strong>

                                <span>
                                    Try another product, brand or category.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </form>
    );

    return (
        <>
            <header className="home-nav">
                <div className="home-nav-container">
                    <div className="home-nav-left">
                        <button type="button" className="home-nav-logo" aria-label="Go to Salar home" onClick={handleLogoClick}>
                            <span className="home-nav-logo-aura" />
                            <img src="/assets/img/logos/salarlogo.png" alt="Salar" />
                        </button>
                        <div className="home-nav-location-wrapper" ref={locationRef}>
                            <button type="button" className={`home-nav-location-trigger ${locationOpen ? "active" : ""}`} onClick={handleLocationOpen}>
                                <span className="home-nav-location-icon">
                                    <MapPin size={18} strokeWidth={1.9} />
                                </span>
                                <span className="home-nav-location-copy">
                                    <span className="home-nav-delivery-label">
                                        <strong> Deliver to </strong>
                                        <span className="home-nav-location-status" />
                                    </span>
                                    <small>{selectedAddress.address}</small>
                                </span>
                                <ChevronDown size={14} className={locationOpen ? "home-nav-rotate" : ""} />
                            </button>
                            {locationOpen && (
                                <div className="home-nav-location-dropdown">
                                    <div className="home-nav-location-header">
                                        <span>
                                            <Navigation size={19} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <h4> Choose delivery address </h4>
                                            <p> Select where you want your orders delivered. </p>
                                        </div>
                                    </div>
                                    <button type="button" className="home-nav-current-location" onClick={detectCurrentLocation} disabled={detectingLocation}>
                                        <span>
                                            <Crosshair size={18} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <strong>
                                                {detectingLocation ? "Detecting location..." : "Use my current location"}
                                            </strong>
                                            <small> Automatically detect using your device </small>
                                        </div>
                                        <ChevronRight size={15} />
                                    </button>
                                    <button type="button" className="home-nav-manual-toggle" onClick={() => setManualLocationOpen((prev) => !prev)}>
                                        <span>
                                            <MapPin size={17} strokeWidth={1.9} /> Enter address manually
                                        </span>
                                        <ChevronDown size={15} className={manualLocationOpen ? "home-nav-rotate" : ""} />
                                    </button>
                                    {manualLocationOpen && (
                                        <div className="home-nav-manual-location">
                                            <div className="home-nav-manual-input">
                                                <Search size={16} strokeWidth={1.8} />
                                                <input type="text" value={manualLocation}
                                                    placeholder="Enter area, address or pincode"
                                                    onChange={(event) => setManualLocation(event.target.value)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") { saveManualLocation(); }
                                                    }} />
                                            </div>
                                            <button type="button" onClick={saveManualLocation}>
                                                Apply
                                            </button>
                                        </div>
                                    )}
                                    <div className="home-nav-divider" />
                                    <div className="home-nav-saved-header">
                                        <strong> Saved addresses </strong>
                                        <small> {displayAddresses.length} addresses </small>
                                    </div>
                                    <div className="home-nav-saved-list">
                                        {displayAddresses.map((address) => {
                                            const active = String(selectedAddress.id) === String(address.id);
                                            return (
                                                <button type="button" key={address.id} className={`home-nav-saved-address ${active ? "selected" : ""}`} onClick={() => handleSelectAddress(address)}>
                                                    <span className="home-nav-address-icon">
                                                        {address.type === "Work" ? (
                                                            <Store size={16} strokeWidth={1.8} />
                                                        ) : (
                                                            <MapPin size={16} strokeWidth={1.8} />
                                                        )}
                                                    </span>
                                                    <span className="home-nav-address-details">
                                                        <span> <strong> {address.label} </strong>
                                                            <em> {address.type} </em>
                                                        </span>
                                                        <small> {address.address} </small>
                                                    </span>
                                                    {active && (
                                                        <Check size={16} />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={`home-nav-center ${variant === "detail" ? "detail" : ""}`}>
                        {variant === "home" ? (
                            <>
                                <div className="home-nav-mode-row">
                                    {shoppingModes.map(
                                        (item) => (
                                            <button type="button" key={item.id}
                                                className={`home-nav-mode ${shoppingMode === item.id ? "active" : ""}`}
                                                onClick={() => setShoppingMode(item.id)}>
                                                <span> {item.icon} </span>
                                                <div>
                                                    <strong>{item.label}</strong>
                                                    <small> {item.subtitle} </small>
                                                </div>
                                            </button>
                                        )
                                    )}
                                </div>
                                <div className="home-nav-search-wrapper">
                                    {renderSearchBar()}
                                </div>
                            </>
                        ) : (
                            <div className="home-nav-detail-search-row">
                                {renderSearchBar("home-nav-detail-search")}
                            </div>
                        )}
                    </div>
                    <div className="home-nav-right">
                        <button
                            type="button"
                            className={`home-nav-global-toggle ${globalShopping ? "active" : ""}`}
                            onClick={() => setGlobalShopping((prev) => !prev)}
                            aria-pressed={globalShopping}>
                            <span className="home-nav-global-icon">
                                <Globe2 size={17} strokeWidth={1.9} />
                            </span>
                            <span className="home-nav-global-copy">
                                <small> Shopping Mode </small>
                                <strong> Global</strong>
                            </span>
                            <span className="home-nav-global-check">
                                {globalShopping && (<Check size={12} strokeWidth={2.6} />)}
                            </span>
                        </button>
                        <div className="home-nav-locale-wrapper" ref={localeRef}>
                            <button type="button" className={`home-nav-locale-trigger ${localeOpen ? "active" : ""}`}
                                onClick={handleLocaleOpen}>
                                <span className="home-nav-right-icon">
                                    <Languages size={18} strokeWidth={1.8} />
                                </span>
                                <span className="home-nav-right-copy">
                                    <small>Preferences</small>
                                    <strong> {selectedCountry.flag} {selectedCountry.code} · {selectedLanguage.shortName} · {currency}</strong>
                                </span>
                                <ChevronDown size={14} className={localeOpen ? "home-nav-rotate" : ""} />
                            </button>
                            {localeOpen && (
                                <div className="home-nav-locale-dropdown">
                                    <div className="home-nav-locale-header">
                                        <span>
                                            <Languages size={20} />
                                        </span>
                                        <div>
                                            <h4> Country, Language & Currency </h4>
                                            <p> Personalize your Salar shopping experience. </p>
                                        </div>
                                    </div>
                                    <div className="home-nav-preference-group">
                                        <label> Country </label>
                                        <div className="home-nav-preference-wrapper">
                                            <button type="button" className="home-nav-preference-trigger"
                                                onClick={() => { setCountryOpen((prev) => !prev); setLanguageOpen(false); setCurrencyOpen(false); }}
                                                aria-haspopup="listbox"
                                                aria-expanded={countryOpen}>
                                                <span>
                                                    <b className="home-nav-preference-flag"> {selectedCountry.flag} </b>
                                                    <span>
                                                        <small> Shopping country </small>
                                                        <strong> {selectedCountry.name} </strong>
                                                    </span>
                                                </span>
                                                <ChevronDown size={15} className={countryOpen ? "home-nav-rotate" : ""} />
                                            </button>
                                            {countryOpen && (
                                                <div className="home-nav-preference-options home-nav-country-options"
                                                    role="listbox"
                                                    aria-label="Select country">
                                                    {countries.map(
                                                        (item) => (<button type="button" key={item.code}
                                                            className={country === item.code ? "selected" : ""}
                                                            onClick={() => { setCountry(item.code); setCountryOpen(false); }}
                                                            role="option"
                                                            aria-selected={country === item.code}>
                                                            <span>
                                                                <b className="home-nav-preference-flag"> {item.flag} </b>
                                                                <span className="home-nav-country-copy">
                                                                    <strong> {item.name} </strong>
                                                                    <small> {item.code} </small>
                                                                </span>
                                                            </span>
                                                            {country === item.code && <Check size={15} strokeWidth={2.4} />}
                                                        </button>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="home-nav-preference-group">
                                        <label> Language </label>
                                        <div className="home-nav-preference-wrapper">
                                            <button type="button" className="home-nav-preference-trigger"
                                                onClick={() => { setLanguageOpen((prev) => !prev); setCountryOpen(false); setCurrencyOpen(false); }}
                                                aria-haspopup="listbox"
                                                aria-expanded={languageOpen}>
                                                <span>
                                                    <b className="home-nav-preference-flag"> {selectedLanguage.flag} </b>
                                                    <span>
                                                        <small> Display language </small>
                                                        <strong> {selectedLanguage.name} </strong>
                                                    </span>
                                                </span>
                                                <ChevronDown size={15} className={languageOpen ? "home-nav-rotate" : ""} />
                                            </button>
                                            {languageOpen && (
                                                <div className="home-nav-preference-options home-nav-language-options"
                                                    role="listbox"
                                                    aria-label="Select language">
                                                    {languages.map(
                                                        (item) => (<button type="button" key={item.name}
                                                            className={language === item.name ? "selected" : ""}
                                                            onClick={() => { setLanguage(item.name); setLanguageOpen(false); }}
                                                            role="option"
                                                            aria-selected={language === item.name}>
                                                            <span>
                                                                <b className="home-nav-preference-flag"> {item.flag} </b>
                                                                <span className="home-nav-language-copy">
                                                                    <strong> {item.name} </strong>
                                                                    <small> {item.shortName} </small>
                                                                </span>
                                                            </span>
                                                            {language === item.name && <Check size={15} />}
                                                        </button>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="home-nav-preference-group">
                                        <label> Currency </label>
                                        <div className="home-nav-preference-wrapper">
                                            <button type="button" className="home-nav-preference-trigger"
                                                onClick={() => { setCurrencyOpen((prev) => !prev); setCountryOpen(false); setLanguageOpen(false); }}>
                                                <span>
                                                    <b> {selectedCurrency.symbol} </b>
                                                    <span>
                                                        <small> Shopping currency </small>
                                                        <strong> {selectedCurrency.code} </strong>
                                                    </span>
                                                </span>

                                                <ChevronDown size={15} className={currencyOpen ? "home-nav-rotate" : ""} />
                                            </button>
                                            {currencyOpen && (
                                                <div className="home-nav-preference-options">
                                                    {currencies.map(
                                                        (item) => (
                                                            <button type="button" key={item.code}
                                                                className={currency === item.code ? "selected" : ""}
                                                                onClick={() => {
                                                                    setCurrency(item.code);
                                                                    setCurrencyOpen(false);
                                                                }}>
                                                                <span>
                                                                    <b> {item.symbol} </b>
                                                                    <span className="home-nav-currency-copy">
                                                                        <strong> {item.code} </strong>
                                                                        <small> {item.name} </small>
                                                                    </span>
                                                                </span>
                                                                {currency === item.code && <Check size={15} />}
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <button type="button" className="home-nav-apply"
                                        onClick={() => {
                                            setLocaleOpen(false);
                                            setCountryOpen(false);
                                            setLanguageOpen(false);
                                            setCurrencyOpen(false);
                                        }}>Apply Preferences
                                    </button>
                                </div>
                            )}
                        </div>
                        <span className="home-nav-right-divider" />
                        {isAuthenticated && (
                            <>
                                <button type="button" className="home-nav-right-action"
                                    onClick={() => navigate("/wishlist")}>
                                    <span className="home-nav-right-icon">
                                        <Heart size={20} strokeWidth={1.8} />
                                        <span className="home-nav-count"> {wishlistCount > 99 ? "99+" : wishlistCount} </span>
                                    </span>
                                    <span className="home-nav-right-copy">
                                        <small>Saved Items </small>
                                        <strong>Wishlist</strong>
                                    </span>
                                </button>
                                <button type="button" className="home-nav-right-action" onClick={handleCartClick} aria-label={`Go to cart with ${cartCount} items`}>
                                    <span className="home-nav-right-icon">
                                        <ShoppingCart size={21} strokeWidth={1.8} />
                                        {cartCount > 0 && (
                                            <span className="home-nav-count">
                                                {cartCount > 99 ? "99+" : cartCount}
                                            </span>
                                        )}
                                    </span>
                                    <span className="home-nav-right-copy">
                                        <small> My Cart </small>
                                        <strong>
                                            {selectedCurrency.symbol}
                                            {formatCartPrice(cartTotal)}
                                        </strong>
                                    </span>
                                </button>
                            </>
                        )}
                        <div className="home-nav-profile-wrapper" ref={profileRef}>
                            <button
                                type="button"
                                className={`home-nav-profile-trigger ${profileOpen ? "active" : ""}`}
                                onClick={handleProfileOpen}
                                aria-label="User profile and account menu"
                            >
                                <Menu size={18} strokeWidth={1.8} />
                                <span>
                                    {isAuthenticated && userInitial ? (
                                        <b style={{ fontSize: "14px", fontWeight: 700, lineHeight: 1 }}>
                                            {userInitial}
                                        </b>
                                    ) : (
                                        <User size={17} strokeWidth={1.8} />
                                    )}
                                </span>
                            </button>
                            {profileOpen && (
                                <div className={`home-nav-profile-dropdown ${isAuthenticated ? "" : "guest"}`}>
                                    <div className="home-nav-profile-header">
                                        <span>
                                            {isAuthenticated && userInitial ? (
                                                <b style={{ fontSize: "16px", fontWeight: 700, lineHeight: 1 }}>
                                                    {userInitial}
                                                </b>
                                            ) : (
                                                <User size={20} />
                                            )}
                                        </span>
                                        <div>
                                            <h4>{isAuthenticated ? userName : "Welcome to Salar"}</h4>
                                            <p>{isAuthenticated ? userSubtitle : "Manage your account and shopping."}</p>
                                        </div>
                                    </div>
                                    {!isAuthenticated ? (
                                        <div className="home-nav-auth">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setProfileOpen(false);
                                                    navigate("/SignIn");
                                                }}
                                            >
                                                Sign In
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setProfileOpen(false);
                                                    navigate("/SignUp");
                                                }}
                                            >
                                                Create Account
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="home-nav-divider" />
                                            <div className="home-nav-profile-menu">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/Dashboard");
                                                    }}
                                                >
                                                    <span> <UserRound size={17} /></span>
                                                    <div>
                                                        <strong> My Account </strong>
                                                        <small> Profile, preferences and account settings </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/orders");
                                                    }}
                                                >
                                                    <span> <Package size={17} /></span>
                                                    <div>
                                                        <strong> My Orders </strong>
                                                        <small> Track, return and manage purchases </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/SalarPay");
                                                    }}
                                                >
                                                    <span> <WalletCards size={17} /></span>
                                                    <div>
                                                        <strong> My Wallet</strong>
                                                        <small> Wallet balance, payments and refunds </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/Wishlist");
                                                    }}
                                                >
                                                    <span> <Heart size={17} /> </span>
                                                    <div>
                                                        <strong> My Wishlist </strong>
                                                        <small> View your saved products </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/ManageAddress");
                                                    }}
                                                >
                                                    <span> <MapPin size={17} /> </span>
                                                    <div>
                                                        <strong> Saved Address</strong>
                                                        <small> Manage delivery addresses </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/Subscriptions");
                                                    }}
                                                >
                                                    <span> <Bookmark size={17} /></span>
                                                    <div>
                                                        <strong> My Subscriptions</strong>
                                                        <small> Manage active plans and subscriptions </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/ReferralCommission");
                                                    }}
                                                >
                                                    <span> <CircleDollarSign size={17} /> </span>
                                                    <div>
                                                        <strong> My Earnings </strong>
                                                        <small> Rewards, earnings and benefits</small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/SalarShopVoucher");
                                                    }}
                                                >
                                                    <span> <ShoppingBag size={17} /> </span>
                                                    <div>
                                                        <strong> My Stuff</strong>
                                                        <small> Saved items and personal shopping activity </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/BrowserHistory");
                                                    }}
                                                >
                                                    <span> <History size={17} /> </span>
                                                    <div>
                                                        <strong> My Browsing History</strong>
                                                        <small> Recently viewed products and categories </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/Offers");
                                                    }}
                                                >
                                                    <span> <Bell size={17} /> </span>
                                                    <div>
                                                        <strong> Notifications</strong>
                                                        <small> Offers, order updates and alerts </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProfileOpen(false);
                                                        navigate("/Tickets");
                                                    }}
                                                >
                                                    <span> <HelpCircle size={17} /></span>
                                                    <div>
                                                        <strong>Help & Support </strong>
                                                        <small> Get help with orders, payments and account issues </small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleLogout}
                                                >
                                                    <span> <LogOut size={17} /> </span>
                                                    <div>
                                                        <strong>Logout </strong>
                                                        <small> Sign out of your Salar account</small>
                                                    </div>
                                                    <ChevronRight size={14} />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <button type="button" className="home-nav-mobile-button" onClick={() => setMobileMenuOpen((prev) => !prev)} aria-label="Toggle navigation">
                            {mobileMenuOpen ? (<X size={20} />) : (<Menu size={20} />)}
                        </button>
                    </div>
                </div>
            </header>
            {mobileMenuOpen && (
                <div className="home-nav-mobile">
                    <button type="button" className={`home-nav-mobile-global ${globalShopping ? "active" : ""}`}
                        onClick={() => setGlobalShopping((prev) => !prev)}>
                        <span> <Globe2 size={16} strokeWidth={1.9} /> Global Shopping</span>
                        <span className="home-nav-mobile-global-check">
                            {globalShopping && (<Check size={12} strokeWidth={2.5} />)}
                        </span>
                    </button>
                    <div className="home-nav-mobile-modes">
                        <button type="button" className={shoppingMode === "shopping" ? "active" : ""} onClick={() => setShoppingMode("shopping")}>
                            Salar Shopping
                        </button>

                        <button type="button" className={shoppingMode === "quick" ? "active" : ""} onClick={() => setShoppingMode("quick")}>
                            Quick Minutes
                        </button>

                        <button type="button" className={shoppingMode === "food" ? "active" : ""} onClick={() => setShoppingMode("food")}>
                            Food
                        </button>
                        <button type="button" className={shoppingMode === "stores" ? "active" : ""} onClick={() => setShoppingMode("stores")}>
                            Stores
                        </button>
                    </div>
                    <form className="home-nav-mobile-search" onSubmit={handleSearchSubmit}>
                        <Search size={17} onClick={() => handleSearch(searchTerm)} style={{ cursor: "pointer" }} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            onFocus={handleSearchFocus}
                            placeholder={getSearchPlaceholder()}
                        />
                    </form>
                    <button type="button" className="home-nav-mobile-row" onClick={handleLocationOpen}>
                        <span> <MapPin size={17} /> Deliver to </span>
                        <strong> {selectedAddress.address} </strong>
                    </button>
                    <button type="button" className="home-nav-mobile-row">
                        <span> <Languages size={17} /> Language </span>
                        <strong> {selectedLanguage.shortName} · {currency} </strong>
                    </button>
                    {isAuthenticated && (
                        <>
                            <button type="button" className="home-nav-mobile-row" onClick={() => { setMobileMenuOpen(false); navigate("/wishlist"); }}>
                                <span> <Heart size={17} /> Wishlist </span>
                                <strong> {wishlistCount} </strong>
                            </button>
                            <button type="button" className="home-nav-mobile-row" onClick={handleCartClick}>
                                <span> <ShoppingCart size={17} /> My Cart
                                    {cartCount > 0 && (<em className="home-nav-mobile-cart-count"> {cartCount} </em>)}
                                </span>
                                <strong> {selectedCurrency.symbol} {formatCartPrice(cartTotal)} </strong>
                            </button>
                        </>
                    )}
                </div>
            )}
        </>
    );
};
export default HomeNavbar;